import { toastError } from '@onestep/coreui/Toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import useCurrentClinic from '../../../hooks/useCurrentClinic';
import useMixpanel from '../../../hooks/useMixpanel';

export const useTutorialActions = () => {
  const { clinic } = useCurrentClinic();
  const queryClient = useQueryClient();
  const { trackClick } = useMixpanel();

  const createEventAction = useMutation({
    mutationFn: async (actionName: string) => {
      try {
        trackClick(`web-tutorial-${actionName}-start`);
        await axios.post('/api/camel/tutorial', {
          action: actionName,
        });
      } catch {
        toastError({ content: 'Failed to create event!' });
      }
    },
  });

  const endEventAction = useMutation({
    mutationFn: async (tourData: { isSkipped: boolean; tourType: string }) => {
      try {
        trackClick({
          action: `web-tutorial-end`,
          data: {
            skipped: tourData?.isSkipped ?? false,
            tourType: tourData?.tourType,
          },
        });
        await axios.post('/api/camel/tutorial/end');
        await invalidateTutorialListQuery();
      } catch {
        toastError({ content: 'Failed to end tutorial!' });
      }
    },
  });

  const resetEventAction = useMutation({
    mutationFn: async () => {
      try {
        await axios.post('/api/camel/tutorial/reset');
        await invalidateTutorialListQuery();
      } catch {
        toastError({ content: 'Failed to reset tutorial!' });
      }
    },
  });

  const invalidateTutorialListQuery = async () => {
    await queryClient.invalidateQueries({
      queryKey: ['camel', 'tutorial', 'list', { clinicUuid: clinic?.uuid }],
    });
  };

  return {
    createEvent: createEventAction.mutateAsync,
    endEvent: endEventAction.mutateAsync,
    resetEvent: resetEventAction.mutateAsync,
  };
};
